import type { Accessor, Component } from 'solid-js'
import { For, Show } from 'solid-js'
import type {
  UsersMediaItem,
  UsersEventsItem,
  UsersForumItem,
  UsersChatItem
} from '../../../features/engagement/domain/models/engagement-users'
import Table, { type TableColumn, TableStatus } from './Table'
import TablePagination from './TablePagination'
import { Icon } from '@iconify-icon/solid'
import EngagementUsersTableRow from './EngagementUsersTableRow'

export type EngagementUserType = UsersMediaItem | UsersEventsItem | UsersForumItem | UsersChatItem

interface EngagementUsersTableProps {
  users: EngagementUserType[]
  totalUsers: number
  page: Accessor<number>
  pageSize: number
  onPageChange: (page: number) => void
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  category?: 'media' | 'events' | 'forum' | 'chat'
  sortColumn: Accessor<string | null>
  sortDirection: Accessor<'asc' | 'desc'>
  isLoading?: boolean
}

const EngagementUsersTable: Component<EngagementUsersTableProps> = (props) => {
  const startIndex = () => (props.page() - 1) * props.pageSize

  const tableOrderBy = () => {
    const column = props.sortColumn()
    if (!column) {
      return undefined
    }
    return `${column}:${props.sortDirection()}`
  }

  const setTableOrderBy = (orderBy: string | undefined): void => {
    // The Table component cycles desc -> asc -> undefined. In this screen we keep desc -> asc -> desc.
    if (!orderBy) {
      const currentColumn = props.sortColumn()
      if (currentColumn) {
        props.onSort?.(currentColumn, 'desc')
      }
      return
    }

    const [column, direction] = orderBy.split(':')
    if (column && (direction === 'asc' || direction === 'desc')) {
      props.onSort?.(column, direction)
    }
  }

  const metricColumns: TableColumn[] = (() => {
    if (props.category === 'events') {
      return [
        { id: 'buy_now_clicks', sortable: true },
        { id: 'presale_codes', sortable: true },
        { id: 'meet_and_greet', sortable: true },
        { id: 'vip', sortable: true },
        { id: 'rsvp', sortable: true }
      ]
    }

    if (props.category === 'forum') {
      return [
        { id: 'views', sortable: true },
        { id: 'posts', sortable: true },
        { id: 'comments', sortable: true }
      ]
    }

    if (props.category === 'chat') {
      return [{ id: 'messages', sortable: true }]
    }

    // media + default
    return [
      { id: 'songs_played', sortable: true },
      { id: 'videos_viewed', sortable: true },
      { id: 'livestream_participation', sortable: true }
    ]
  })()

  const tableColumns: TableColumn[] = [
    {
      id: 'index',
      class: 'w-12 text-primary-600',
      header: <Icon icon="heroicons:user" class="text-[1.25rem]" />
    },
    {
      id: 'user',
      class: 'text-primary-600'
    },
    ...metricColumns
  ]

  const status = () => {
    if (props.isLoading && props.users.length === 0) {
      return TableStatus.Init
    }
    if (props.isLoading) {
      return TableStatus.Loading
    }
    if (props.users.length === 0) {
      return TableStatus.NoData
    }
    return TableStatus.Loaded
  }

  return (
    <div class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div class="border-t border-gray-200">
        <div class="overflow-x-auto">
          <Table
            status={status}
            columns={tableColumns}
            orderBy={tableOrderBy}
            setOrderBy={setTableOrderBy}
            tableId="users"
          >
            <For each={props.users}>
              {(user, index) => (
                <EngagementUsersTableRow user={user} rowNumber={startIndex() + index() + 1} category={props.category} />
              )}
            </For>
          </Table>
        </div>
        <Show when={props.totalUsers > props.pageSize}>
          <TablePagination
            totalCount={props.totalUsers}
            pageSize={props.pageSize}
            page={props.page}
            setPage={props.onPageChange}
          />
        </Show>
      </div>
    </div>
  )
}

export default EngagementUsersTable
